import {
  DefaultEmployeeInterface,
  DefaultUserInterface,
  UserNamesInterface,
  UserOnboardingPostRequest,
} from '@interfaces';
import apiClient from '../../../api-client';

export class UsersService {
  public async getUsers(
    companyId: number,
    search?: string
  ): Promise<DefaultUserInterface[]> {
    try {
      const response = await apiClient.get(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/users${
          search ? `?search=${search}` : ''
        }`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching all users: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getUserNames(
    companyId: number,
    searchTerm: string = ''
  ): Promise<UserNamesInterface[]> {
    try {
      const response = await apiClient.get(
        `${
          process.env.GATSBY_SSO_BACKEND_API_URL
        }/company/${companyId}/users/names${
          searchTerm ? `?search=${searchTerm}` : ''
        }`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching usernames: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getUserRelations(
    companyId: number,
    userId: number,
    relations: string[]
  ) {
    try {
      const response = await apiClient.get(
        `${
          process.env.GATSBY_SSO_BACKEND_API_URL
        }/company/${companyId}/users/${userId}/relations?relations=${relations.join(
          ','
        )}`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching user relations: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  // NOT USED
  public async getUser(companyId: number, userId: number) {
    try {
      const response = await apiClient.get(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/users/${userId}`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching user: ${error.response?.statusText || error.message}`
      );
    }
  }

  public async updateUser(
    companyId: number,
    userId: number,
    updatedData: Partial<User>
  ) {
    try {
      const response = await apiClient.patch(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/users/${userId}`,
        updatedData
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error updating user: ${error.response?.statusText || error.message}`
      );
    }
  }

  public async removeFromGroup(companyId: number, userId: number) {
    try {
      const response = await apiClient.patch(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/users/${userId}/removefromgroup`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error removing user from group: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async createUser(companyId: number, newUserData: Partial<User>) {
    try {
      const response = await apiClient.post(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/users`,
        newUserData
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error creating user: ${error.response?.statusText || error.message}`
      );
    }
  }

  public async deleteUser(companyId: number, userId: number) {
    try {
      const response = await apiClient.delete(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/users/${userId}`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error deleting user: ${error.response?.statusText || error.message}`
      );
    }
  }

  public async getUserSims(companyId: number, userId: number) {
    try {
      const url = `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/sims/user/${userId}`;

      const response = await apiClient.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching all user sims: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getImpersonateOptions(
    companyId: number,
    search?: string
  ): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (search) queryParams.append('search', search);

      const url = `${
        process.env.GATSBY_SSO_BACKEND_API_URL
      }/company/${companyId}/impersonate/options${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;

      const response = await apiClient.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching impersonate options: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getAuthUser() {
    try {
      const response = await apiClient.get(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/auth/user`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error getting user: ${error.response?.statusText || error.message}`
      );
    }
  }

  public async logout(): Promise<void> {
    try {
      const response = await apiClient.post(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/auth/logout`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error logging out: ${error.response?.statusText || error.message}`
      );
    }
  }

  public async getAllEmployees(): Promise<void> {
    try {
      const response = await apiClient.get(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/backoffice/employees/get`
      );

      return response.data;
    } catch (error) {
      console.log(error);
    }
  }

  public async createOnboarding(
    companyId: number,
    onboardingData: UserOnboardingPostRequest
  ) {
    const url = `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/onboarding`;

    try {
      const response = await apiClient.post(url, { ...onboardingData });
      return response.data;
    } catch (error) {
      console.log(error);
      return 'Er ging iets mis';
    }
  }

  public async getEmployeeById(
    employeeId: number
  ): Promise<DefaultEmployeeInterface> {
    try {
      const response = await apiClient.get(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/backoffice/employees/get/${employeeId}`
      );

      return response.data.employee;
    } catch (error) {
      console.log(error);
      throw new Error(
        `Error fetching employee: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  public async getVpnUsers() {
    try {
      const response = await apiClient.get(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/1/users/vpn`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching all users: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async approveVpnUser(userId: string, companyId: number) {
    try {
      const response = await apiClient.patch(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/users/vpn`,
        {
          userId: userId,
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching all users: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async updateVpnUser(userId: string, companyId: number, update: any) {
    try {
      const response = await apiClient.put(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/users/vpn`,
        {
          userId: userId,
          ...update,
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching all users: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getVpnPeers(companyId: number) {
    try {
      const response = await apiClient.get(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/users/vpn/peers`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching all users: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getVpnEvents(companyId: number, type: string) {
    try {
      const response = await apiClient.get(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/users/vpn/events`,
        {
          params: {
            type: type,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching all users: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getAccessiblePeers(companyId: number, peerId: string) {
    try {
      const response = await apiClient.get(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/users/vpn/peers/${peerId}/accessible`,
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching all users: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }
}
